import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { Button, Container } from '@mui/material';
import axios from 'axios';
import { baseURL } from '../base_url';
import { PuffLoader } from 'react-spinners';
import { SiNissan } from "react-icons/si";
import * as XLSX from "xlsx";
import moment from 'moment-timezone';

const FetchNissanFleet = () => {
    const [movies, setMovies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");

    const getNissanVehicles = () => {
        setLoading(true);

        axios.get(baseURL + "/fetch_nissan_fleet")
            .then((res) => {
                console.log("Nissan API response:", res.data);

                if (res.data.success) {
                    const vehicles = res.data.data.vehicles || [];

                    setVehicles(vehicles);
                    setTotalCount(res.data.data.totalCount || 0);
                } else {
                    setVehicles([]);
                }

                // Reset to first page on every fetch
                setCurrentPage(1);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Nissan vehicle API error:", error);
                setVehicles([]);
                setCurrentPage(1);
                setLoading(false);
            });
    };

    // useEffect(() => {
    //     getNissanVehicles();
    // }, []);

    // Filter the full response by VIN (partial, case-insensitive)
    const normalizedSearch = searchTerm.trim().toUpperCase();
    const filteredVehicles = normalizedSearch
        ? vehicles.filter((vehicle) =>
            String(vehicle.vin || "").toUpperCase().includes(normalizedSearch)
        )
        : vehicles;

    // Pagination calculations (on filtered results)
    const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredVehicles.length);
    const paginatedVehicles = filteredVehicles.slice(startIndex, endIndex);

    // Keep current page in range if data or page size changes
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const clearSearch = () => {
        setSearchTerm("");
        setCurrentPage(1);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const goToPage = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Show up to 5 page numbers around the current page
    const getPageNumbers = () => {
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = start + maxVisible - 1;

        if (end > totalPages) {
            end = totalPages;
            start = Math.max(1, end - maxVisible + 1);
        }

        const pages = [];
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const formatDateTime = (value) => {
        if (!value) return null;
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        });
    };

    const ConvertToTimeZone = (datetime, loc_id) => {
        const timezone = {
            '6': 'America/Denver',
            '20': 'America/Denver',
            '3': 'America/New_York',
            '5': 'America/New_York',
            '4': 'America/Chicago',
            '2': 'America/Toronto',
            '1': 'America/Edmonton',
            '7': 'America/Vancouver',
            '9': 'America/Toronto',
            '8': 'America/Toronto',
            '12': 'America/Toronto',
            '21': 'Asia/Dubai'
        };

        const tzName = timezone[String(loc_id)];
        if (!datetime || !tzName) return null;

        const convertedTime = moment(datetime).tz(tzName).format('YYYY-MM-DD hh:mm:ss A z');
        return convertedTime;
    };

    const getLastApiResponseDisplay = (vehicle) => {
        if (!vehicle.last_api_response) return null;

        // Location timezone first, fall back to browser-local time if location isn't mapped
        return (
            ConvertToTimeZone(vehicle.last_api_response, vehicle.location_id) ||
            formatDateTime(vehicle.last_api_response)
        );
    };

    const downloadNissanExcel = () => {
        if (!vehicles || vehicles.length === 0) {
            alert("No Nissan vehicles available to download.");
            return;
        }

        const excelData = vehicles.map((vehicle, index) => ({
            "#": index + 1,
            "VIN": vehicle.vin || "",
            "Location": vehicle.location_code || "",
            "Last API Response": getLastApiResponseDisplay(vehicle) || "",
            "Make": vehicle.make || "",
            "Model": vehicle.model || "",
            "Year": vehicle.year || "",
            "Color": vehicle.color || "",
            "Country": vehicle.country || "",
            "Enrollment Status": vehicle.enrollmentStatus || "",
            "Enrolled For Data": vehicle.enrolledForData ? "Yes" : "No",
            "Account ID": vehicle.accountId || ""
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Nissan Vehicles");

        XLSX.writeFile(
            workbook,
            `Nissan_Vehicles_${new Date().toISOString().split("T")[0]}.xlsx`
        );
    };

    return(
        <div className='body_class'>
            {loading ? (
                <div className="loader-overlay">
                    <div className="loader">
                    <PuffLoader color="#00308F" loading={true} size={200} />
                    </div>
                </div>
            ) : ("")}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b px-5 py-4">

                    <div className="flex items-center gap-3">
                        <div className='d-flex gap-2'>
                            <div className="h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                                <SiNissan size={25} />
                                
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">
                                     Nissan Vehicles
                                </h2>
                                {/* <p className="text-sm text-gray-500">
                                    Active vehicles enrolled for telematics
                                </p> */}
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                onClick={getNissanVehicles}
                                disabled={loading}
                                className="btn btn-primary"
                            >
                                {loading ? "Fetching Fleet..." : "Fetch Fleet"}
                            </button>

                            <button
                                type="button"
                                onClick={downloadNissanExcel}
                                disabled={loading || vehicles.length === 0}
                                className="btn btn-success"
                            >
                                Download Excel
                            </button>
                        </div>

                    </div>

                    <div className="rounded-lg bg-gray-50 px-4 py-2 text-center">
                        <div className="text-xl font-bold text-gray-900">
                            {vehicles.length}
                        </div>

                        <div className="text-xs text-gray-500">
                            Enrolled VINs
                        </div>
                    </div>

                </div>
                <div className="p-5">
                    {loading ? (
                        <div className="py-10 text-center text-gray-500">
                            Loading Nissan vehicles...
                        </div>
                    ) : vehicles.length === 0 ? (
                        <div className="py-10 text-center text-gray-500">
                            No enrolled Nissan vehicles found.
                        </div>
                    ) : (
                        <>
                            {/* VIN search */}
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                                <div className="d-flex align-items-center gap-2" style={{ maxWidth: "420px", width: "100%" }}>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        placeholder="Search by VIN..."
                                        className="form-control font-mono"
                                    />
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            onClick={clearSearch}
                                            className="btn btn-outline-secondary"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>

                                {normalizedSearch && (
                                    <div className="text-sm text-gray-600">
                                        {filteredVehicles.length} of {vehicles.length} VINs match
                                    </div>
                                )}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b text-sm text-gray-500">
                                            <th className="px-4 py-3">#</th>
                                            <th className="px-4 py-3">VIN</th>
                                            <th className="px-4 py-3">Location</th>
                                            <th className="px-4 py-3">Last API Response</th>
                                            <th className="px-4 py-3">Model</th>
                                            <th className="px-4 py-3">Year</th>
                                            <th className="px-4 py-3">Color</th>
                                            <th className="px-4 py-3">Enrollment Status</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paginatedVehicles.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                                                    No VIN matches "{searchTerm.trim()}".
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedVehicles.map((vehicle, index) => (
                                                <tr
                                                    key={vehicle.vin || startIndex + index}
                                                    className="border-b hover:bg-gray-50"
                                                >
                                                    <td className="px-4 py-3 text-sm">
                                                        {startIndex + index + 1}
                                                    </td>

                                                    <td className="px-4 py-3 font-mono text-sm font-medium">
                                                        {vehicle.vin}
                                                    </td>

                                                    <td className="px-4 py-3 text-sm">
                                                        {vehicle.location_code ? (
                                                            vehicle.location_code
                                                        ) : (
                                                            <span className="text-gray-400">Unassigned</span>
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                                                        {vehicle.last_api_response ? (
                                                            getLastApiResponseDisplay(vehicle)
                                                        ) : (
                                                            <span className="text-gray-400">No data</span>
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-3 text-sm">
                                                        {vehicle.model}
                                                    </td>

                                                    <td className="px-4 py-3 text-sm">
                                                        {vehicle.year}
                                                    </td>

                                                    <td className="px-4 py-3 text-sm">
                                                        {vehicle.color || "N/A"}
                                                    </td>

                                                    <td className="px-4 py-3">
                                                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${
                                                            vehicle.enrollmentStatus === "ENROLLED"
                                                                ? "bg-green-50 text-green-700"
                                                                : "bg-red-50 text-red-700"
                                                        }`}>
                                                            {vehicle.enrollmentStatus || "UNKNOWN"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {filteredVehicles.length > 0 && (
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-4">
                                    <div className="d-flex align-items-center gap-2 text-sm text-gray-600">
                                        <span>Rows per page:</span>
                                        <select
                                            value={pageSize}
                                            onChange={handlePageSizeChange}
                                            className="form-select form-select-sm"
                                            style={{ width: "auto" }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <span>
                                            Showing {startIndex + 1}–{endIndex} of {filteredVehicles.length}
                                        </span>
                                    </div>

                                    <div className="d-flex align-items-center gap-1">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => goToPage(1)}
                                            disabled={currentPage === 1}
                                        >
                                            «
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => goToPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            ‹ Prev
                                        </button>

                                        {getPageNumbers().map((page) => (
                                            <button
                                                key={page}
                                                type="button"
                                                className={`btn btn-sm ${
                                                    page === currentPage
                                                        ? "btn-primary"
                                                        : "btn-outline-secondary"
                                                }`}
                                                onClick={() => goToPage(page)}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => goToPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next ›
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => goToPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                        >
                                            »
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
        </div>
        </div>
      )
      
}

export default FetchNissanFleet;